/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Athlete Avatar Component
 */

import { useContext, useEffect, useState } from "react";
import { Avatar, AvatarFallback, Badge } from "@lemonadetraining/web-ui";
import { AuthContext } from "@lemonadetraining/web-shared";
import VariantAvatarImage from "@lemonadetraining/web-shared/components/VariantAvatarImage";

interface AthleteAvatarProps {
  AthleteUID?: string;
  Name: string;
  avatar?: string;
  size?: "sm" | "default" | "lg";
  showName?: boolean;
}

const AthleteAvatar = ({
  AthleteUID,
  Name,
  avatar,
  size = "default",
  showName = true,
}: AthleteAvatarProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const authCtx = useContext(AuthContext) as any;
  const [avatarPicURL, setAvatarPicURL] = useState<string | undefined>(avatar);

  useEffect(() => {
    const loadPic = async () => {
      if (AthleteUID && authCtx?.getProfilePicURL) {
        try {
          const url = await authCtx.getProfilePicURL(AthleteUID);
          if (url) {
            setAvatarPicURL(url);
          }
        } catch {
          // Silently handle missing profile pictures - they'll use default avatar
          console.debug("Profile picture not found for user:", AthleteUID);
        }
      }
    };

    loadPic();
  }, [authCtx, AthleteUID]);

  // Generate initials from name
  const getInitials = (name: string): string => {
    if (!name) return "?";
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div className="flex items-center gap-2">
      <Avatar size={size}>
        <VariantAvatarImage src={avatarPicURL} alt={Name} />
        <AvatarFallback>{getInitials(Name)}</AvatarFallback>
      </Avatar>
      {showName && (
        <Badge variant="outline" className="font-normal">
          {Name}
        </Badge>
      )}
    </div>
  );
};

export default AthleteAvatar;
